<template>
  <div class="select-autocomplete" role="combobox" aria-haspopup="true">
    <div class="form-group select-autocomplete-button">
      <button
        v-if="!selected"
        :disabled="disabled"
        @click.prevent="toggleDropdown"
        @keydown="onButtonKeyDown"
        class="select-disabled form-control"
        type="button"
        tabindex="0">
        <i
          v-if="ico"
          :class="ico"
          class="fa"></i> {{ placeholder }}

        <span>
          <i
            v-if="!loading && !showDropdown"
            class="fa fa-angle-down"></i>
          <i
            v-if="!loading && showDropdown"
            class="fa fa-angle-up"></i>
          <i
            v-if="loading"
            class="fa fa-spinner fa-spin price-fare-rules size-16"></i>
        </span>
      </button>
      <button
        v-if="selected"
        @click.prevent="clearSelected"
        class="select-disabled form-control"
        type="button"
        tabindex="0">
        <i
          v-if="ico"
          :class="ico"
          class="fa"></i> {{ selected[listKey] }}

        <span>
          <i class="fa fa-close"></i>
        </span>
      </button>
    </div>

    <div
      v-show="showDropdown"
      class="select-autocomplete-input">
      <input
        v-model="search"
        :aria-controls="name + '-list-box'"
        @keydown="onKeyDown"
        @input="onChange"
        @focus="onFocus"
        :name="name"
        :maxlength="(selected && selected.hasOwnProperty(listKey)) ? selected[listKey].length : 122"
        autocomplete="off"
        class="form-control"
        type="text"
        ref="search"
        aria-autocomplete="list"
        tabindex="0"
        placeholder="" />
      <span class="select-autocomplete-helper">
        Digite 3 ou mais caracteres
      </span>

      <span
        v-if="!loading && error.length"
        class="select-autocomplete-helper">
        {{ error }}
      </span>
    </div>

    <div
      v-show="showDropdown"
      class="select-autocomplete-dropdown clearfix">
      <ul
        v-if="searchList.length"
        class="select-autocomplete-list"
        role="listbox">
        <li
          role="option"
          v-for="(item, index) in searchList"
          :key="`select-${name}-list-item-${index}`"
          :class="{ 'active' : index === focusItem }">
          <a
            @click.prevent="handleSelected(item)"
            href="#">
            <slot
              name="image"
              :imageSrc="item[imageKey]" />
            {{ item[listKey] }}
          </a>
        </li>
      </ul>
    </div>

    <div
      @click.prevent="toggleDropdown"
      v-if="showDropdown"
      class="select-autocomplete-mask"></div>
  </div>
</template>

<script>
import debounce from '@/utils/debounce'
import replaceSpecialChars from '@/filters/replaceSpecialChars'

export default {
  props: {
    loading: {
      type: Boolean,
      default: () => false
    },
    ico: {
      type: String
    },
    list: {
      type: Array,
      default: () => []
    },
    listKey: {
      type: String,
      default: () => ''
    },
    listValue: {
      type: String,
      default: () => 'id'
    },
    name: {
      type: String,
      default: () => ''
    },
    placeholder: {
      type: String,
      default: () => ''
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    error: {
      type: String,
      default: () => ''
    },
    selectedValue: {
      type: Object,
      default: () => null
    },
    imageKey: {
      type: String,
      default: () => ''
    }
  },
  data () {
    return {
      showDropdown: false,
      search: null,
      searchList: [],
      selected: null,
      focusItem: 0,
      hasEvent: this.$listeners && Object.prototype.hasOwnProperty.call(this.$listeners, 'event')
    }
  },
  watch: {
    list (data) {
      if ( data.length > 0 ) {
        this.$set(this, 'searchList', [...data])
      }
    },
    selectedValue (data) {
      if (data === null) {
        this.selected = null
      } else {
        this.selected = data
      }
    }
  },
  methods: {
    toggleDropdown () {
      this.showDropdown = !this.showDropdown
      this.$nextTick(() => {
        this.$refs.search.focus()
      })
    },
    clearSelected () {
      this.handleSelected(null)
      this.toggleDropdown()

      if (!this.hasEvent) {
        this.searchList = [ ...this.list ]
      }
    },
    onFocus () {
      /**/
    },
    onChange: debounce(function (event) {
      if (event.target.value.length >= 3) {
        if (this.hasEvent) {
          this.$emit('event', event.target.value)
        } else {
          this.handleSearch()
        }
      }

      if (event.target.value.length === 0 && !this.hasEvent) {
        this.searchList = [ ...this.list ]
      }
    }, 400),
    onButtonKeyDown (e) {
      const key = e.keyCode

      switch (key) {
        case 40: // DOWN
          this.toggleDropdown()
        break

        case 13: // ENTER
          this.toggleDropdown()
        break
      }
    },
    onKeyDown (e) {
      const key = e.keyCode
      const height = (this.$el.querySelectorAll('.select-autocomplete-list')[0] !== undefined) ? this.$el.querySelectorAll('.select-autocomplete-list')[0].querySelectorAll('li')[0].clientHeight : 0

      if (this.searchList.length === 0) {
        return
      }

      switch (key) {
        case 40: // DOWN
          this.focusItem ++
          this.$el.querySelector('.select-autocomplete-list').scrollTop = (height * this.focusItem)
        break

        case 38: // UP
          this.focusItem --
          this.$el.querySelector('.select-autocomplete-list').scrollTop = (height * this.focusItem)
        break

        case 13: // ENTER
          this.handleSelected(this.searchList[this.focusItem])
          e.preventDefault()
        break

        case 27: // ESC
          this.handleSelected(null)
          e.preventDefault()
        break
      }

      // CURSOR OUT OF RANGE
      const listLength = this.searchList.length - 1
      this.focusItem = ( this.focusItem > listLength ) ? 0 : ( this.focusItem < 0 ) ? listLength : this.focusItem
    },
    handleSearch () {
      const source = [ ...this.searchList ]
      const sourceKeys = this.listKey.split(',')
      const search = this.search

      const searchList = source.filter(item =>
        sourceKeys.some(key => {
          return (Object.prototype.hasOwnProperty.call(item, key)
            && (item[key] && String(replaceSpecialChars(item[key]).toUpperCase()).indexOf(replaceSpecialChars(search).toUpperCase()) !== -1))
        })
      )

      this.searchList = searchList
    },
    handleSelected (selected) {
      this.selected = selected ? {
        ...selected
      } : null
      this.$emit('input', this.selected)

      this.searchList = []
      this.search = null
      this.showDropdown = false
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/scss/components/fields/SelectAutocomplete';
</style>
